import React, { useState, useEffect } from "react";
import { Tooltip, Select, Tag, Card, Row, Col, Table, Modal, Button, message, Alert, Input, DatePicker, Checkbox, Divider, Dropdown } from "antd";
import { 
  InfoCircleOutlined, 
  SettingOutlined, 
  EditOutlined,
  CheckOutlined,
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  FileExcelOutlined 
} from "@ant-design/icons";
import moment from "moment";
import * as XLSX from "xlsx";
import { debounce } from "lodash";
import ManagerService from "../../services/ManagerService";
import "bootstrap/dist/css/bootstrap.min.css";

const QuanLyXeComponent = () => {
  // State dữ liệu xe và lọc
  const [xeData, setXeData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Modal lịch sử xe (du_lieu_xe)
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedXe, setSelectedXe] = useState(null);
  const [duLieuXe, setDuLieuXe] = useState([]);
  const [loadingDuLieuXe, setLoadingDuLieuXe] = useState(false);
  // State cho ngày được chọn trong modal (mặc định hôm nay)
  const [selectedDate, setSelectedDate] = useState(moment());

  // Định nghĩa mảng cột (key + label) để đồng bộ giữa Table chính và menu chọn cột
  const columnKeys = [
    { key: "Id", label: "ID" },
    { key: "Serial", label: "Số Seri" },
    { key: "Type", label: "Loại" },
    { key: "ModelName", label: "Tên model" },
    { key: "Bs", label: "Biển số" },
    { key: "PhoneOfDevice", label: "Số ĐTTB" },
    { key: "Sgtvt", label: "Sgtvt" },
    { key: "Version", label: "Phiên bản" },
    { key: "DriverId", label: "ID tài xế" },
    { key: "OwnerPhone", label: "Số ĐT" },
    { key: "DeviceNote", label: "Ghi chú" },
    { key: "IsCamera", label: "Camera" },
    { key: "HongNgoai", label: "Hồng Ngoại" },
    { key: "LoaiTaxiKinhDoanh", label: "Loại taxi kinh doanh" },
    { key: "TenLoaiTaxiKinhDoanh", label: "Tên loại taxi" },
  ];

  // State hiển thị cột
  const [visibleColumns, setVisibleColumns] = useState(columnKeys.map(c => c.key));

  // Lấy dữ liệu từ bảng thong_tin_xe khi component mount
  useEffect(() => {
    const fetchXeData = async () => {
      setLoading(true);
      try {
        const response = await ManagerService.getThongTinXe();
        if (response.success) {
          setXeData(response.data);
          setFilteredData(response.data);
        } else {
          message.error("Lỗi khi lấy dữ liệu xe");
        }
      } catch (error) {
        message.error("Lỗi khi lấy dữ liệu xe");
      } finally {
        setLoading(false);
      }
    };
    fetchXeData();
  }, []);

  // Hàm lấy dữ liệu lịch sử xe – mở rộng để nhận thêm date (định dạng "YYYY-MM-DD")
  const fetchDuLieuXe = async (serial, date) => {
    setLoadingDuLieuXe(true);
    try {
      const response = await ManagerService.getDuLieuXe(serial, date);
      if (response.success) {
        setDuLieuXe(response.data);
      } else {
        message.error("Lỗi khi lấy dữ liệu lịch sử xe");
      }
    } catch (error) {
      message.error("Lỗi khi lấy dữ liệu lịch sử xe");
    } finally {
      setLoadingDuLieuXe(false);
    }
  };

  // Khi double click vào dòng xe, mở modal và gọi API với ngày hôm nay
  const handleRowDoubleClick = (record) => {
    setSelectedXe(record);
    setSelectedDate(moment()); // đặt mặc định là hôm nay
    fetchDuLieuXe(record.Serial, moment().format("YYYY-MM-DD"));
    setModalVisible(true);
  };

  // Hàm lọc dữ liệu theo cột (sử dụng debounce)
  const handleSearch = debounce((value, dataIndex) => {
    const filtered = xeData.filter(item =>
      item[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase())
    );
    setFilteredData(filtered);
  }, 300);

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Tìm kiếm`}
          value={selectedKeys[0]}
          onChange={(e) => {
            setSelectedKeys(e.target.value ? [e.target.value] : []);
            handleSearch(e.target.value, dataIndex);
          }}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
      </div>
    )
  });

  // Định nghĩa các cột cho bảng xe với tên cột tiếng Việt
  const columnsThongTinXe = [
    {
      title: "ID",
      dataIndex: "Id",
      key: "Id",
      width: "5%"
    },
    { 
      title: "Số Seri", 
      dataIndex: "Serial", 
      key: "Serial", 
      width: "8%",
      ...getColumnSearchProps("Serial")
    },
    { 
      title: "Loại", 
      dataIndex: "Type", 
      key: "Type", 
      width: "8%",
      ...getColumnSearchProps("Type")
    },
    { 
      title: "Tên model", 
      dataIndex: "ModelName", 
      key: "ModelName", 
      width: "12%",
      ...getColumnSearchProps("ModelName")
    },
    { 
      title: "Biển số", 
      dataIndex: "Bs", 
      key: "Bs", 
      width: "8%",
      ...getColumnSearchProps("Bs")
    },
    { 
      title: "Số ĐTTB", 
      dataIndex: "PhoneOfDevice", 
      key: "PhoneOfDevice", 
      width: "10%"
    },
    { 
      title: "Sgtvt", 
      dataIndex: "Sgtvt", 
      key: "Sgtvt", 
      width: "8%"
    },
    { 
      title: "Phiên bản", 
      dataIndex: "Version", 
      key: "Version", 
      width: "8%"
    },
    { 
      title: "ID tài xế", 
      dataIndex: "DriverId", 
      key: "DriverId", 
      width: "8%"
    },
    { 
      title: "Số ĐT", 
      dataIndex: "OwnerPhone", 
      key: "OwnerPhone", 
      width: "8%"
    },
    { 
      title: "Ghi chú", 
      dataIndex: "DeviceNote", 
      key: "DeviceNote", 
      width: "12%"
    },
    { 
      title: "Camera", 
      dataIndex: "IsCamera", 
      key: "IsCamera", 
      width: "5%",
      render: (value) =>
        value ? <CheckCircleOutlined style={{ color: "green", fontSize: "18px" }} /> 
              : <CloseCircleOutlined style={{ color: "red", fontSize: "18px" }} />
    },
    { 
      title: "Hồng Ngoại", 
      dataIndex: "HongNgoai", 
      key: "HongNgoai", 
      width: "5%",
      render: (value) => (value ? "Có" : "Không")
    },
    { 
      title: "Loại taxi kinh doanh", 
      dataIndex: "LoaiTaxiKinhDoanh", 
      key: "LoaiTaxiKinhDoanh", 
      width: "10%"
    },
    { 
      title: "Tên loại taxi", 
      dataIndex: "TenLoaiTaxiKinhDoanh", 
      key: "TenLoaiTaxiKinhDoanh", 
      width: "10%"
    },
  ].filter(col => visibleColumns.includes(col.key));

  // Định nghĩa cột bảng lịch sử xe
  const columnsDuLieuXe = [
    {
      title: "T/g cập nhật",
      dataIndex: "TimeUpdate",
      key: "TimeUpdate",
      width: "15%",
      render: (val) => val ? moment(val).format("YYYY-MM-DD HH:mm:ss") : "",
      sorter: (a, b) => moment(a.TimeUpdate) - moment(b.TimeUpdate),
      defaultSortOrder: 'descend'
    },
    {
      title: "Tọa độ",
      dataIndex: "Location",
      key: "Location",
      width: "10%",
      render: (location) => {
        if (location && typeof location === 'object') {
          return `[${location.Lat}, ${location.Lng}]`;
        }
        return location;
      }
    },
    {
      title: "Địa chỉ",
      dataIndex: "Location",
      key: "Address",
      width: "10%",
      render: (location) => {
        if (location && typeof location === 'object') {
          return location.Address || "";
        }
        return "";
      }
    },
    { title: "Tốc độ", dataIndex: "Speed", key: "Speed", width: "5%",
      render: (value) => (value != null ? `${value} km/h` : "") },
    { title: "Sức", dataIndex: "Power", key: "Power", width: "8%" },
    { title: "Nhiệt độ 1", dataIndex: "Temperature", key: "Temperature", width: "8%",
      render: (value) => (value != null ? `${(value / 10).toFixed(1)} °C` : "") },
    { title: "Nhiệt độ 2", dataIndex: "Temperature2", key: "Temperature2", width: "8%",
      render: (value) => (value != null ? `${(value / 10).toFixed(1)} °C` : "") },
    { title: "Tình trạng GPS", dataIndex: "GpsStatus", key: "GpsStatus", width: "8%",
      render: (value) =>
        value ? <CheckCircleOutlined style={{ color: "green", fontSize: "18px" }} /> 
              : <CloseCircleOutlined style={{ color: "red", fontSize: "18px" }} /> },
    { title: "Khoảng cách", dataIndex: "Distance", key: "Distance", width: "8%" },
    { title: "Khoảng cách Tổng", dataIndex: "TotalDistance", key: "TotalDistance", width: "8%" }
  ];

  // Hàm export dữ liệu ra Excel (sử dụng thư viện XLSX)
  const exportToExcel = () => {
    const formattedData = xeData.map(item => ({
      "ID": item.Id,
      "Số Seri": item.Serial,
      "Loại": item.Type,
      "Tên modal": item.ModelName,
      "Biển số": item.Bs,
      "Số điện thoại thiết bị": item.PhoneOfDevice,
      "Sgtvt": item.Sgtvt,
      "Phiên bản": item.Version,
      "ID tài xế": item.DriverId,
      "Số điện thoại": item.OwnerPhone,
      "Ghi chú": item.DeviceNote,
      "Camera": item.Camera,
      "Hồng ngoại": item.HongNgoai,
      "Loại Taxi kinh doanh": item.LoaiTaxiKinhDoanh,
      "Tên loại Taxi": item.TenLoaiTaxiKinhDoanh,
    }));
    const ws = XLSX.utils.json_to_sheet(formattedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ThongTinXe");
    const today = moment().format("YYYYMMDD");
    XLSX.writeFile(wb, `ThongTinXe_${today}.xlsx`);
  };

  // Toggle cột hiển thị
  const toggleColumnVisibility = (columnKey) => {
    setVisibleColumns((prev) =>
      prev.includes(columnKey)
        ? prev.filter((col) => col !== columnKey)
        : [...prev, columnKey]
    );
  };

  // Menu Dropdown cột hiển thị (tiếng Việt)
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const columnMenuContent = (
    <div style={{ padding: 10, background: "#fff", borderRadius: 4, boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
      {/* Checkbox Chọn tất cả / Bỏ chọn tất cả */}
      <Checkbox
        checked={visibleColumns.length === columnKeys.length}
        onChange={(e) =>
          setVisibleColumns(e.target.checked
            ? columnKeys.map(c => c.key)
            : []
          )
        }
      >
        Chọn tất cả
      </Checkbox>
      <br />
      <Checkbox
        checked={visibleColumns.length === 0}
        onChange={(e) => setVisibleColumns([])}
      >
        Bỏ chọn tất cả
      </Checkbox>
      <Divider style={{ margin: "8px 0" }} />

      {/* Các checkbox cho từng cột */}
      {columnKeys.map((col) => (
        <div key={col.key} style={{ marginBottom: 4 }}>
          <Checkbox
            checked={visibleColumns.includes(col.key)}
            onChange={() => toggleColumnVisibility(col.key)}
          >
            {col.label}
          </Checkbox>
        </div>
      ))}
      <Button
        type="primary"
        onClick={() => setDropdownVisible(false)}
        style={{ marginTop: 10, width: "100%" }}
      >
        Xác nhận
      </Button>
    </div>
  );

  const columnDropdown = (
    <Dropdown
      overlay={columnMenuContent}
      trigger={["click"]}
      visible={dropdownVisible}
      onVisibleChange={(flag) => setDropdownVisible(flag)}
    >
      <Button icon={<SettingOutlined />} style={{ marginBottom: 10 }}>
        Chọn cột hiển thị
      </Button>
    </Dropdown>
  );

  return (
    <div className="container-fluid" style={{ padding: "20px" }}>
      <h2 className="text-center mb-4">QUẢN LÝ XE</h2>

      {/* Thông báo hướng dẫn cho người dùng */}
      <Alert 
        message={
          <span>
            <InfoCircleOutlined style={{ marginRight: "8px" }} />
            Nhấn <strong>đúp chuột</strong> vào dòng xe để xem chi tiết lịch sử xe.
          </span>
        }
        type="info"
        showIcon
        className="mb-4"
      />

      <div className="d-flex flex-wrap justify-content-between mb-3">
        {columnDropdown}
        <Button icon={<FileExcelOutlined />} onClick={exportToExcel}>
          Xuất Excel
        </Button>
      </div>

      <Table
        dataSource={filteredData}
        columns={columnsThongTinXe}
        loading={loading}
        rowKey="Id"
        bordered
        pagination={{ pageSize: 10 }}
        scroll={{ x: "100%" }}
        onRow={(record) => ({
          onDoubleClick: () => handleRowDoubleClick(record)
        })}
      />

      <Modal
        title={
          <div>
            Lịch sử xe: {selectedXe ? selectedXe.Serial : ""}
            <DatePicker
              style={{ marginLeft: 20 }}
              hint={selectedDate}
              format="YYYY-MM-DD"
              onChange={(date) => {
                setSelectedDate(date);
                if (selectedXe && date) {
                  // Gọi API với ngày được chọn (định dạng "YYYY-MM-DD")
                  fetchDuLieuXe(selectedXe.Serial, date.format("YYYY-MM-DD"));
                }
              }}
            />
          </div>
        }
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Đóng
          </Button>
        ]}
        width="90%"
      >
        <Table
          dataSource={duLieuXe}
          columns={columnsDuLieuXe}
          loading={loadingDuLieuXe}
          rowKey="id"
          bordered
          pagination={{ pageSize: 10 }}
        />
      </Modal>
    </div>
  );
};

export default QuanLyXeComponent;